import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { RenewMembersCard } from '../components/RenewMembersCard';
import {
  EmptyState,
  ErrorNotice,
  Field,
  Notice,
  SelectField,
  Spinner,
  formatMoney,
} from '../components/ui';
import type {
  MembershipCategory,
  MembershipYear,
  MembershipYearSummary,
} from '../types';

/**
 * One membership year: its dates, its rates and its BDI bands.
 *
 * There is deliberately no "edit rate" control anywhere on this page — only
 * add and delete. That absence is the point: category_rate and bdi_band rows
 * are never updated in place in the API either (see
 * MembershipYearRepository), so there is nothing here for an edit form to
 * call. Fixing a wrong number is delete-then-add, and delete itself stops
 * working the moment the year opens — `year.can_delete_rates` is exactly
 * that boundary, computed server side, not guessed at here.
 */
export function RatesYearPage() {
  const { id } = useParams<{ id: string }>();
  const [year, setYear] = useState<MembershipYear | null>(null);
  const [categories, setCategories] = useState<MembershipCategory[] | null>(null);
  const [otherYears, setOtherYears] = useState<MembershipYearSummary[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function load() {
    api
      .get<{ year: MembershipYear }>(`/membership-years/${id}`)
      .then((result) => setYear(result.year))
      .catch(setError);
  }

  useEffect(load, [id]);

  useEffect(() => {
    api
      .get<{ categories: MembershipCategory[] }>('/membership-categories')
      .then((result) => setCategories(result.categories))
      .catch(() => undefined);
    api
      .get<{ years: MembershipYearSummary[] }>('/membership-years')
      .then((result) => setOtherYears(result.years))
      .catch(() => undefined);
  }, []);

  function flash(message: string) {
    setNotice(message);
    window.setTimeout(() => setNotice(null), 4000);
  }

  if (error !== null && year === null) {
    return (
      <>
        <p className="muted small">
          <Link to="/rates">← Back to rates &amp; settings</Link>
        </p>
        <ErrorNotice error={error} />
      </>
    );
  }

  if (year === null) return <Spinner label="Loading year" />;

  const pricedCategoryIds = new Set(year.category_rates.map((r) => r.membership_category_id));
  const unpricedCategories = (categories ?? []).filter(
    (c) => c.is_active && !pricedCategoryIds.has(c.id),
  );

  return (
    <>
      <p className="muted small">
        <Link to="/rates">← Back to rates &amp; settings</Link>
      </p>

      <div className="page-head">
        <h1>{year.year}</h1>
        <div className="head-actions">
          {year.is_current ? (
            <span className="pill pill-active">current year</span>
          ) : (
            <ActivateButton yearId={year.id} onActivated={(y) => { setYear(y); flash('This is now the current year.'); }} onError={setError} />
          )}
        </div>
      </div>

      {notice ? <Notice kind="success">{notice}</Notice> : null}
      <ErrorNotice error={error} />

      <DatesCard year={year} onSaved={setYear} />

      <div className="card">
        <h2>Category rates</h2>
        <p className="muted small">
          {year.can_delete_rates
            ? 'This year has not opened yet, so a rate can still be removed and re-added if it was wrong.'
            : 'This year has already opened, so its rates are locked. Adding a rate for a category that has never had one is still fine.'}
        </p>

        {year.category_rates.length === 0 ? (
          <EmptyState title="No rates set yet" />
        ) : (
          <table className="members">
            <thead>
              <tr>
                <th scope="col">Category</th>
                <th scope="col">Amount</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {year.category_rates.map((rate) => (
                <tr key={rate.id}>
                  <td data-label="Category">
                    {rate.category_name}
                    {!rate.category_is_active ? <span className="muted"> (retired category)</span> : null}
                  </td>
                  <td data-label="Amount">{formatMoney(rate.amount)}</td>
                  <td data-label="">
                    {year.can_delete_rates ? (
                      <DeleteRateButton
                        yearId={year.id}
                        categoryId={rate.membership_category_id}
                        onDeleted={setYear}
                        onError={setError}
                      />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {unpricedCategories.length > 0 ? (
          <AddRateForm yearId={year.id} categories={unpricedCategories} onAdded={setYear} />
        ) : null}
      </div>

      <div className="card">
        <h2>BDI bands</h2>
        <p className="muted small">Banded on declared colony count. The first band is normally free.</p>

        {year.bdi_bands.length === 0 ? (
          <EmptyState title="No bands set yet" />
        ) : (
          <table className="members">
            <thead>
              <tr>
                <th scope="col">Up to</th>
                <th scope="col">Premium</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {year.bdi_bands.map((band) => (
                <tr key={band.id}>
                  <td data-label="Up to">{band.max_colonies} colonies</td>
                  <td data-label="Premium">{formatMoney(band.premium)}</td>
                  <td data-label="">
                    {year.can_delete_rates ? (
                      <DeleteBandButton yearId={year.id} bandId={band.id} onDeleted={setYear} onError={setError} />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <AddBandForm yearId={year.id} onAdded={setYear} />
      </div>

      {otherYears !== null && otherYears.filter((y) => y.id !== year.id).length > 0 ? (
        <CopyRatesCard
          yearId={year.id}
          otherYears={otherYears.filter((y) => y.id !== year.id)}
          onCopied={(y) => { setYear(y); flash('Rates and bands copied across.'); }}
        />
      ) : null}

      {/*
        Last on the page deliberately. Rolling the membership forward only
        makes sense once the rates and bands above are right — it freezes them
        onto a subscription for every member, and a wrong figure here is one
        that has to be unpicked one person at a time.
      */}
      <RenewMembersCard yearId={year.id} year={year.year} />
    </>
  );
}

function ActivateButton({
  yearId,
  onActivated,
  onError,
}: {
  yearId: number;
  onActivated: (year: MembershipYear) => void;
  onError: (error: unknown) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function activate() {
    setBusy(true);
    onError(null);
    try {
      const result = await api.post<{ year: MembershipYear }>(`/membership-years/${yearId}/activate`);
      onActivated(result.year);
    } catch (failure) {
      onError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button type="button" className="primary" disabled={busy} onClick={() => void activate()}>
      {busy ? 'Making current…' : 'Make this the current year'}
    </button>
  );
}

function DatesCard({ year, onSaved }: { year: MembershipYear; onSaved: (year: MembershipYear) => void }) {
  const [opensOn, setOpensOn] = useState(year.opens_on);
  const [dueOn, setDueOn] = useState(year.due_on);
  const [bdiCutoff, setBdiCutoff] = useState(year.bdi_cutoff_date);
  const [delayDays, setDelayDays] = useState(String(year.bdi_delay_days));
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const dirty =
    opensOn !== year.opens_on ||
    dueOn !== year.due_on ||
    bdiCutoff !== year.bdi_cutoff_date ||
    delayDays !== String(year.bdi_delay_days);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await api.put<{ year: MembershipYear }>(`/membership-years/${year.id}`, {
        opens_on: opensOn,
        due_on: dueOn,
        bdi_cutoff_date: bdiCutoff,
        bdi_delay_days: Number(delayDays),
      });
      onSaved(result.year);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card form wide" onSubmit={onSubmit}>
      <h2>Dates</h2>
      <p className="muted small">
        Dates are scheduling, not money — unlike rates, they can be changed at any time,
        current year or not.
      </p>

      <ErrorNotice error={error} />

      <div className="grid">
        <Field label="Opens on" type="date" value={opensOn} onChange={(e) => setOpensOn(e.target.value)} />
        <Field label="Renewal due" type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
        <Field label="BDI cutoff" type="date" value={bdiCutoff} onChange={(e) => setBdiCutoff(e.target.value)} />
        <Field
          label="BDI delay (days)"
          type="number"
          min={0}
          value={delayDays}
          onChange={(e) => setDelayDays(e.target.value)}
          hint="Added to the payment date when it lands after the BDI cutoff."
        />
      </div>

      <div className="actions">
        <button type="submit" disabled={busy || !dirty}>
          {busy ? 'Saving…' : 'Save dates'}
        </button>
      </div>
    </form>
  );
}

function AddRateForm({
  yearId,
  categories,
  onAdded,
}: {
  yearId: number;
  categories: MembershipCategory[];
  onAdded: (year: MembershipYear) => void;
}) {
  const [categoryId, setCategoryId] = useState(String(categories[0]?.id ?? ''));
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await api.post<{ year: MembershipYear }>(`/membership-years/${yearId}/category-rates`, {
        membership_category_id: Number(categoryId),
        amount: Number(amount),
      });
      setAmount('');
      onAdded(result.year);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form inline-form" onSubmit={onSubmit}>
      <ErrorNotice error={error} />
      <div className="grid">
        <SelectField label="Category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>
        <Field
          label="Amount"
          type="number"
          min={0}
          step={0.01}
          required
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </div>
      <div className="actions">
        <button type="submit" disabled={busy}>
          {busy ? 'Adding…' : 'Add rate'}
        </button>
      </div>
    </form>
  );
}

function DeleteRateButton({
  yearId,
  categoryId,
  onDeleted,
  onError,
}: {
  yearId: number;
  categoryId: number;
  onDeleted: (year: MembershipYear) => void;
  onError: (error: unknown) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function remove() {
    setBusy(true);
    onError(null);
    try {
      const result = await api.del<{ year: MembershipYear }>(`/membership-years/${yearId}/category-rates/${categoryId}`);
      onDeleted(result.year);
    } catch (failure) {
      onError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button type="button" className="link" disabled={busy} onClick={() => void remove()}>
      {busy ? 'Removing…' : 'Remove'}
    </button>
  );
}

function AddBandForm({ yearId, onAdded }: { yearId: number; onAdded: (year: MembershipYear) => void }) {
  const [maxColonies, setMaxColonies] = useState('');
  const [premium, setPremium] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await api.post<{ year: MembershipYear }>(`/membership-years/${yearId}/bdi-bands`, {
        max_colonies: Number(maxColonies),
        premium: Number(premium),
        sort_order: Number(maxColonies),
      });
      setMaxColonies('');
      setPremium('');
      onAdded(result.year);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form inline-form" onSubmit={onSubmit}>
      <ErrorNotice error={error} />
      <div className="grid">
        <Field
          label="Up to how many colonies"
          type="number"
          min={1}
          required
          value={maxColonies}
          onChange={(e) => setMaxColonies(e.target.value)}
        />
        <Field
          label="Premium"
          type="number"
          min={0}
          step={0.01}
          required
          value={premium}
          onChange={(e) => setPremium(e.target.value)}
        />
      </div>
      <div className="actions">
        <button type="submit" disabled={busy}>
          {busy ? 'Adding…' : 'Add band'}
        </button>
      </div>
    </form>
  );
}

function DeleteBandButton({
  yearId,
  bandId,
  onDeleted,
  onError,
}: {
  yearId: number;
  bandId: number;
  onDeleted: (year: MembershipYear) => void;
  onError: (error: unknown) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function remove() {
    setBusy(true);
    onError(null);
    try {
      const result = await api.del<{ year: MembershipYear }>(`/membership-years/${yearId}/bdi-bands/${bandId}`);
      onDeleted(result.year);
    } catch (failure) {
      onError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button type="button" className="link" disabled={busy} onClick={() => void remove()}>
      {busy ? 'Removing…' : 'Remove'}
    </button>
  );
}

function CopyRatesCard({
  yearId,
  otherYears,
  onCopied,
}: {
  yearId: number;
  otherYears: MembershipYearSummary[];
  onCopied: (year: MembershipYear) => void;
}) {
  const [sourceId, setSourceId] = useState(String(otherYears[0]?.id ?? ''));
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function copy() {
    setError(null);
    setBusy(true);
    try {
      const result = await api.post<{ year: MembershipYear }>(
        `/membership-years/${yearId}/copy-rates/${sourceId}`,
      );
      onCopied(result.year);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>Copy rates from another year</h2>
      <p className="muted small">
        Fills in whatever this year is still missing from the year you pick. Anything
        this year already has is left alone — it never overwrites an existing rate.
      </p>

      <ErrorNotice error={error} />

      <div className="grid">
        <SelectField label="Copy from" value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
          {otherYears.map((y) => (
            <option key={y.id} value={y.id}>
              {y.year}
            </option>
          ))}
        </SelectField>
      </div>
      <div className="actions">
        <button type="button" disabled={busy} onClick={() => void copy()}>
          {busy ? 'Copying…' : 'Copy rates and bands'}
        </button>
      </div>
    </div>
  );
}
